import React, { ReactElement, FC, useState } from 'react';
import { Alert, Tab, Table } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import Page from '../../components/Page/Page';
import Panel from '../../components/Panel/Panel';
import PropertyDisplay from '../../components/PropertyDisplay/PropertyDisplay';
import SectionTitle from '../../components/SectionTitle/SectionTitle';
import StatefulTabs from '../../components/StatefulTabs/StatefulTabs';
import Title from '../../components/Title/Title';
import { companyAgreementConstants } from '../../constants/companyAgreementConstants';
import { permConst } from '../../constants/permConst';
import { transferRightsConstants } from '../../constants/transferRightsConstants';
import { useUserProfile } from '../../hooks/reduxHooks';
import { getCompaniesInBulk, getCompanyAgreements } from '../../services/companiesService';
import { getComponentDetails, getComponentOrderPrice, getDigitalOrdersPlatformLegend } from '../../services/digitalCodesService';
import { getUsersInBulk } from '../../services/usersService';
import { formatCurrency } from '../../utils/currencyUtils';
import { dateFormat, formatDate } from '../../utils/dateUtils';
import { determineActiveRightTransfer } from '../../utils/transferRightsUtils';
import { platformAgreements, getActiveAgreements } from '../../utils/companyUtils';
import { isAuthorized } from '../../utils/userUtils';
import { cancelErrorToast } from '../../utils/toaster';
import { getAllUserAndCompanyIDs } from './ComponentDetail.helpers';
import ComponentProperties from './views/ComponentProperties';
import ComponentPricesProperties from './views/ComponentPricesProperties';
import DeclaredPriceTab from './views/DeclaredPriceTab';
import CIBPriceRequestsTab from './views/CIBPriceRequestsTab';


const ComponentDetail: FC<unknown> = (): ReactElement => {
	const match = useParams<{ nsUid: string }>();
	const { nsUid }: { nsUid: string } = match;

	const userProfile = useUserProfile();

	const [selectedTab, setSelectedTab] = useState<string | null>(null);

	const componentQuery = useQuery(['getComponentDetail', nsUid], () =>
		getComponentDetails(nsUid),
	);
	const componentData = {...componentQuery?.data?.data};
	const [allUserIDs, allCompanyIDs] = getAllUserAndCompanyIDs(componentData);
	const token = componentQuery.isSuccess &&
		!componentQuery.isFetching &&
		(Date.now() - componentQuery.dataUpdatedAt < 60000) && // token expires in 1 min
		componentQuery.data.headers['x-pdb-authorization'];

	const usersQuery = useQuery(
		['getUsersInBulk', ...allUserIDs],
		() =>
			getUsersInBulk(
				allUserIDs,
				['user_name', 'login_id'],
				token,
			),
		{ enabled: !!token && !!allUserIDs.length, staleTime: Infinity },
	);
	const users = usersQuery.data?.data;

	const companiesQuery = useQuery(
		['getCompanyInBulk', ...allCompanyIDs],
		() =>
			getCompaniesInBulk(
				allCompanyIDs,
				['company_name'],
				token,
			),
		{ enabled: !!token && !!allCompanyIDs.length, staleTime: Infinity },
	);
	const companies = companiesQuery.data?.data;

	const platformLegendsQuery = useQuery(
		'getDigitalOrdersPlatformLegend',
		() => getDigitalOrdersPlatformLegend(),
		{ staleTime: 60000 },
	);
	const platformLegends = platformLegendsQuery.data?.data;
	const cibPurposeId = platformLegends?.find((p) => p.purpose === 'Code-in-Box')?.purpose_id;
	const premiumSkuPurposeId = platformLegends?.find((p) => p.purpose === 'Premium SKU')?.purpose_id;

	const showCIBPriceRequestsTab = !!componentData?.cib_price_requests;
	const showInvestorRewards = componentData?.component?.product_type === 'HAC';

	const cibPriceQuery = useQuery(
		['getComponentOrderPrice', nsUid, cibPurposeId],
		() => getComponentOrderPrice(nsUid, cibPurposeId!, 1),
		{ enabled: showCIBPriceRequestsTab && !!nsUid && cibPurposeId != null, staleTime: Infinity, useErrorBoundary: false, onError: cancelErrorToast },
	);
	const cibPricePerCode = !showCIBPriceRequestsTab
		? undefined
		: cibPriceQuery.isSuccess
			? Number(cibPriceQuery.data?.data?.unit_price) || null
			: cibPriceQuery.isError
				? null
				: undefined;

	const premiumSkuPriceQuery = useQuery(
		['getComponentOrderPrice', nsUid, premiumSkuPurposeId],
		() => getComponentOrderPrice(nsUid, premiumSkuPurposeId!, 1),
		{ enabled: !!nsUid && premiumSkuPurposeId != null, staleTime: Infinity, useErrorBoundary: false, onError: cancelErrorToast },
	);
	const premiumSkuPricePerCode = premiumSkuPriceQuery.isSuccess
		? Number(premiumSkuPriceQuery.data?.data?.unit_price) || null
		: premiumSkuPriceQuery.isError
			? null
			: undefined;

	const hasInternalPermission = isAuthorized(userProfile.permissions, [
		permConst.PRODUCT.VIEW.ALL.THIRD,
	]);

	const digitalNDIDCompanyId = componentData?.product?.product_rights?.find(
		(rh) => rh.right_type === transferRightsConstants.DISTRIBUTION_TYPE.DIGITAL,
	)?.ndid_company_id;
	const ownerNDIDCompanyId = componentData?.product?.product_rights?.find(
		(rh) => rh.right_type === transferRightsConstants.DISTRIBUTION_TYPE.OWNERSHIP,
	)?.ndid_company_id;
	const isDigitalPub = userProfile.companyId === digitalNDIDCompanyId;
	const isOwnerOnly =
		userProfile.companyId !== digitalNDIDCompanyId &&
		userProfile.companyId === ownerNDIDCompanyId;

	const agreementsQuery = useQuery(
		['getCompanyAgreements', digitalNDIDCompanyId],
		() => getCompanyAgreements(digitalNDIDCompanyId),
		{
			enabled: !!(digitalNDIDCompanyId && (hasInternalPermission || isDigitalPub)),
		},
	);

	const areAgreementsOk = (agreements?: CompanyAgreement[]) => {
		const platformCode = componentData?.component?.product_type;
		if (!platformCode || !agreements || !(platformCode in platformAgreements)) {
			return true;
		}
		const digitalAgreements = getActiveAgreements(
			agreements,
			companyAgreementConstants.TYPE.DIGITAL,
			platformCode,
		);
		return Array.isArray(digitalAgreements) && digitalAgreements.length > 0;
	};

	const showDeclaredPriceTab = !!componentData?.declared_price_requests;
	const hasDuplicatePrices = Number(componentData?.component?.retail_prices?.length) > 1;

	const showTabs = showDeclaredPriceTab || showCIBPriceRequestsTab || hasDuplicatePrices;

	const transferPending =
		componentData?.product && 
		'active_rights_transfer' in componentData.product &&
		determineActiveRightTransfer(componentData.product, 'priceRequest');
	const agreementsOk =
		(hasInternalPermission || isDigitalPub) &&
		areAgreementsOk(agreementsQuery.data?.data);
	return (
		<>
			<Page
				isLoading={
					componentQuery.isLoading ||
					agreementsQuery.isLoading ||
					usersQuery.isLoading ||
					companiesQuery.isLoading ||
					platformLegendsQuery.isLoading
				}
				fault={componentQuery.isError}
				documentTitle={componentData?.component?.game_name}
			>
				{!isOwnerOnly &&
					!agreementsOk &&
					(agreementsQuery.isSuccess ? (
						<Alert variant="danger">
							The Global Digital agreement has expired. Please renew the agreement in the
							Nintendo Developer Portal to proceed.
						</Alert>
					) : (
						<Alert variant="danger">
							Agreements could not be retrieved and functions that require a valid
							agreement have been disabled. Reload or contact site support for
							assistance.
						</Alert>
					))}
				{transferPending && (
					<Alert variant="danger">
						The product is pending a rights transfer. Some actions are disabled until
						the transfer is completed or rescinded.
					</Alert>
				)}
				<Breadcrumb>
					<Breadcrumb.Item to="/products">Products</Breadcrumb.Item>
					<Breadcrumb.Item
						to={`/products/${componentData?.product?.product_id}?tab=digital-catalog`}
					>
						{componentData?.product?.name} ({componentData?.product?.game_code})
					</Breadcrumb.Item>
					<Breadcrumb.Item active>{componentData?.component?.game_name}</Breadcrumb.Item>
				</Breadcrumb>
				<Title>
					{componentData?.component?.game_name}
				</Title>
				{showTabs ? (
					<Page.SplitPage>
						<Page.MainCol cols={9}>
							<StatefulTabs
								onSelect={(newTab) => setSelectedTab(newTab)}
								retainPageState
							>
								{showCIBPriceRequestsTab && (
									<Tab
										title="Code-in-Box Price Requests"
										eventKey="cib-price-requests"
									>
										<Page.ContentContainer>
											<CIBPriceRequestsTab
												show={selectedTab === 'cib-price-requests'}
												token={token}
												cibRequests={
													componentData?.cib_price_requests
												}
												canCreateCIBPriceRequest={
													componentData?.can_create_cib_price_request
												}
												nextCIBDeclarationDate={
													componentData?.next_cib_declaration_date != null ? componentData?.next_cib_declaration_date : null
												}
												reloadComponentData={() => componentQuery.refetch()}
												platformCode={componentData?.product?.platform_code}
												component={componentData?.component}
												readOnly={transferPending || !agreementsOk}
												declineReadOnly={transferPending}
												nsUid={nsUid}
												rightsHolderId={digitalNDIDCompanyId}
												users={users}
												companies={companies}
											/>
										</Page.ContentContainer>
									</Tab>
								)}
								{showDeclaredPriceTab && (
									<Tab
										title="Declared Price Requests"
										eventKey="declared-price-requests"
									>
										<Page.ContentContainer>
											<DeclaredPriceTab
												show={selectedTab === 'declared-price-requests'}
												nsUid={nsUid}
												dpRequests={componentData?.declared_price_requests}
												reloadComponentData={() => componentQuery.refetch()}
												component={componentData?.component}
												canCreateDeclaredPriceRequest={
													componentData?.can_create_declared_price_request
												}
												platformCode={componentData?.product?.platform_code}
												readOnly={transferPending || !agreementsOk}
												declineReadOnly={transferPending}
												rightsHolderId={digitalNDIDCompanyId}
												users={users}
												companies={companies}
											/>
										</Page.ContentContainer>
									</Tab>
								)}
								{hasDuplicatePrices && (
									<Tab title="Duplicate Prices" eventKey="duplicate-price">
										<Page.ContentContainer>
											<PropertyDisplay
												label="Available Digital Item"
												noStripes
											>
												{componentData?.component?.game_name}
											</PropertyDisplay>
											<PropertyDisplay label="NSUID" noStripes>
												{componentData?.component?.ns_uid}
											</PropertyDisplay>
											<Table className="base-title-table" striped>
												<thead>
													<tr>
														<th>Start Date</th>
														<th className="col-sm-5">Price</th>
													</tr>
												</thead>
												<tbody>
													{componentData?.component?.retail_prices.map(
														({ price, start_datetime }, index) => (
															<tr key={index + '_price'}>
																<td>
																	{start_datetime
																		? formatDate(
																				start_datetime,
																				dateFormat.DATE,
																		  )
																		: ''}
																</td>
																<td className="col-sm-5">
																	{price
																		? formatCurrency(price)
																		: ''}
																</td>
															</tr>
														),
													)}
												</tbody>
											</Table>
											<p>
												Duplicate prices must be resolved before codes can
												be ordered.
											</p>
										</Page.ContentContainer>
									</Tab>
								)}
							</StatefulTabs>
						</Page.MainCol>
						<Page.SidebarCol cols={3}>
							<Panel>
								<Panel.Body>
							<ComponentProperties componentData={componentData} compact />
								<div style={{ height: '8px', backgroundColor: 'var(--nin-color-ltgray3)', margin: '0 -16px' }} />
								<ComponentPricesProperties compact cibPricePerCode={cibPricePerCode} premiumSkuPricePerCode={premiumSkuPricePerCode} showCIBPrice={showCIBPriceRequestsTab} showInvestorRewards={showInvestorRewards} />
								</Panel.Body>
							</Panel>
						</Page.SidebarCol>
					</Page.SplitPage>
				) : (
					<Page.FullPageCol>
						<SectionTitle>Component Information</SectionTitle>
					<ComponentProperties componentData={componentData} />
					<div style={{ height: '8px', backgroundColor: 'var(--nin-color-ltgray3)', margin: '0 -16px' }} />
					<ComponentPricesProperties cibPricePerCode={cibPricePerCode} premiumSkuPricePerCode={premiumSkuPricePerCode} showCIBPrice={showCIBPriceRequestsTab} showInvestorRewards={showInvestorRewards} />
					</Page.FullPageCol>
				)}
			</Page>
		</>
	);
};
export default ComponentDetail;
